import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useLang, withLang, formatNumber, formatPrice } from "../i18n";
import { colors, fontHeading } from "../theme";
import { whatsappLink } from "../siteConfig";
import { webpSmall, webpSrcSet, MAIN_PHOTO_SIZES } from "../imageUrls";

export default function CarPage({ cars, loading }) {
  const { id } = useParams();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [activeImg, setActiveImg] = useState(0);
  const homePath = withLang("/", lang);

  const car = cars.find((c) => c.id === id);

  if (loading) {
    // Reserve a full screen while cars.json loads so the footer doesn't render
    // in view and then jump down (layout shift / CLS).
    return (
      <div style={{ padding: "120px 40px", minHeight: "100vh", textAlign: "center", color: colors.muted }}>
        {t.loading}
      </div>
    );
  }

  if (!car) {
    return (
      <div style={{ padding: "120px 40px", textAlign: "center" }}>
        <h2 style={{ fontFamily: fontHeading, color: colors.ink }}>{t.notFoundTitle}</h2>
        <p style={{ color: colors.body, marginBottom: 24 }}>{t.notFoundSub}</p>
        <Link to={homePath} style={s.backLink}>
          {t.backToCars}
        </Link>
      </div>
    );
  }

  const title = `${car.brand} ${car.model}`;
  const images = car.images;

  const specs = [
    [t.year, car.year],
    [t.km, `${formatNumber(car.km, lang)} km`],
    [t.transmission, t.transmissionVals[car.transmission]],
    [t.fuel, t.fuelVals[car.fuel]],
    [t.power, `${car.power} CV`],
    [t.color, t.colorVals[car.color]],
    [t.bodyType, t.bodyTypeVals[car.bodyType]],
  ];

  const description = car.description?.[lang];
  const features = car.features?.[lang] || [];

  return (
    <div style={s.page} className="fade">
      <div style={s.container}>
        <Link to={`${homePath}#coches`} style={s.backLink} className="link-hover">
          {t.backToCars}
        </Link>

        <div style={s.grid} className="gallery-grid">
          <div>
            <div style={s.mainPhotoWrap} className="main-photo">
              <picture key={activeImg}>
                <source type="image/webp" srcSet={webpSrcSet(images[activeImg])} sizes={MAIN_PHOTO_SIZES} />
                <img
                  src={images[activeImg]}
                  alt={title}
                  width={1600}
                  height={1200}
                  style={s.mainPhoto}
                  className="fade"
                />
              </picture>
              {activeImg > 0 && (
                <button style={{ ...s.arrow, left: 12 }} onClick={() => setActiveImg((i) => i - 1)} aria-label="Previous">
                  ‹
                </button>
              )}
              {activeImg < images.length - 1 && (
                <button style={{ ...s.arrow, right: 12 }} onClick={() => setActiveImg((i) => i + 1)} aria-label="Next">
                  ›
                </button>
              )}
              <div style={s.counter}>{t.photoOf(activeImg + 1, images.length)}</div>
            </div>
            <div style={s.thumbRow}>
              {images.map((img, i) => (
                <div
                  key={i}
                  className="thumb"
                  style={{
                    ...s.thumb,
                    outline: activeImg === i ? `2px solid ${colors.red}` : "2px solid transparent",
                    opacity: activeImg === i ? 1 : 0.7,
                  }}
                  onClick={() => setActiveImg(i)}
                >
                  <picture>
                    <source type="image/webp" srcSet={webpSmall(img)} />
                    <img
                      src={img}
                      alt={`${title} - foto ${i + 1}`}
                      width={1600}
                      height={1200}
                      loading="lazy"
                      decoding="async"
                      style={s.thumbImg}
                    />
                  </picture>
                </div>
              ))}
            </div>
          </div>

          <div style={s.info}>
            <p style={s.eyebrow}>
              {t.bodyTypeVals[car.bodyType]} · {car.year}
            </p>
            <h1 style={s.title}>{title}</h1>
            <p style={s.price}>{car.price ? formatPrice(car.price, lang) : t.consultPrice}</p>

            <div style={s.divider} />

            <div style={s.specsGrid} className="specs-grid">
              {specs.map(([label, val]) => (
                <div key={label} style={s.specItem}>
                  <span style={s.specLabel}>{label}</span>
                  <span style={s.specVal}>{val}</span>
                </div>
              ))}
            </div>

            <div style={s.divider} />

            <div style={s.ctaCol}>
              <button
                style={s.btnPrimary}
                className="btn-hover"
                onClick={() => navigate(`${homePath}?coche=${encodeURIComponent(title)}#contacto`)}
              >
                {t.contactAboutThisCar}
              </button>
              <a
                href={whatsappLink(t.whatsappCarMsg(title))}
                target="_blank"
                rel="noopener noreferrer"
                style={s.btnWhatsapp}
                className="btn-hover"
              >
                {t.whatsappAboutThisCar}
              </a>
            </div>
          </div>
        </div>

        {(description || features.length > 0) && (
          <div style={s.extraGrid} className="gallery-grid">
            {description && (
              <div>
                <h2 style={s.blockTitle}>{t.descriptionTitle}</h2>
                <p style={s.descriptionText}>{description}</p>
              </div>
            )}
            {features.length > 0 && (
              <div>
                <h2 style={s.blockTitle}>{t.highlightsTitle}</h2>
                <div style={s.featuresGrid}>
                  {features.map((f) => (
                    <div key={f} style={s.featureItem}>
                      <span style={s.featureDot} />
                      {f}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  page: { padding: "44px 40px 100px" },
  container: { maxWidth: 1180, margin: "0 auto" },
  backLink: {
    display: "inline-block",
    background: "none",
    border: "none",
    color: colors.muted,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    marginBottom: 28,
    fontFamily: fontHeading,
    textDecoration: "none",
    padding: 0,
  },
  grid: { display: "grid", gridTemplateColumns: "1.15fr 1fr", gap: 56 },
  mainPhotoWrap: { position: "relative", height: 440, borderRadius: 12, overflow: "hidden", background: colors.bgAlt },
  mainPhoto: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  arrow: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    background: "rgba(255,255,255,0.9)",
    border: "none",
    color: colors.ink,
    fontSize: 26,
    width: 42,
    height: 42,
    borderRadius: "50%",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
  },
  counter: {
    position: "absolute",
    bottom: 14,
    right: 14,
    background: "rgba(0,0,0,0.55)",
    color: "#fff",
    fontSize: 12,
    padding: "4px 10px",
    borderRadius: 20,
  },
  thumbRow: { display: "flex", gap: 8, marginTop: 10, overflowX: "auto", paddingBottom: 4 },
  thumb: { flex: "0 0 84px", height: 62, cursor: "pointer", overflow: "hidden", borderRadius: 6 },
  thumbImg: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  info: { paddingTop: 4 },
  eyebrow: { fontSize: 12, letterSpacing: "0.1em", color: colors.red, marginBottom: 10, fontFamily: fontHeading, fontWeight: 700, textTransform: "uppercase" },
  title: { fontSize: 32, fontWeight: 800, color: colors.ink, margin: "0 0 10px", fontFamily: fontHeading },
  price: { fontSize: 22, color: colors.ink, fontWeight: 700, fontFamily: fontHeading, margin: 0 },
  divider: { borderTop: `1px solid ${colors.border}`, margin: "24px 0" },
  specsGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px 12px" },
  specItem: { display: "flex", flexDirection: "column", gap: 3 },
  specLabel: { fontSize: 11, color: colors.muted, letterSpacing: "0.05em" },
  specVal: { fontSize: 15, color: colors.ink, fontWeight: 600, fontFamily: fontHeading },
  ctaCol: { display: "flex", flexDirection: "column", gap: 12 },
  btnPrimary: {
    background: colors.red,
    color: "#fff",
    border: "none",
    padding: "16px 24px",
    fontSize: 12.5,
    letterSpacing: "0.06em",
    cursor: "pointer",
    fontFamily: fontHeading,
    fontWeight: 700,
    borderRadius: 6,
    textAlign: "center",
  },
  btnWhatsapp: {
    background: colors.whatsapp,
    color: "#fff",
    border: "none",
    padding: "16px 24px",
    fontSize: 12.5,
    letterSpacing: "0.06em",
    cursor: "pointer",
    fontFamily: fontHeading,
    fontWeight: 700,
    borderRadius: 6,
    textAlign: "center",
    textDecoration: "none",
    display: "block",
  },
  extraGrid: {
    display: "grid",
    gridTemplateColumns: "1.15fr 1fr",
    gap: 56,
    marginTop: 52,
    paddingTop: 44,
    borderTop: `1px solid ${colors.border}`,
  },
  blockTitle: { fontSize: 18, fontWeight: 800, color: colors.ink, margin: "0 0 16px", fontFamily: fontHeading },
  descriptionText: { fontSize: 15, lineHeight: 1.85, color: colors.body },
  featuresGrid: { display: "flex", flexDirection: "column", gap: 12 },
  featureItem: { display: "flex", alignItems: "center", gap: 12, fontSize: 14.5, color: colors.ink, fontWeight: 500 },
  featureDot: { width: 7, height: 7, background: colors.red, borderRadius: "50%", flexShrink: 0 },
};
